import { useState, useEffect, useCallback } from 'react';
import { getContacts, createContact, deleteContact, getContactDocuments } from '../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Plus, User, Mail, Phone, MapPin, Trash2, X, Building2, FileText, ChevronDown, ChevronRight, CreditCard, Calendar, Users, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import Sidebar from '../components/layout/Sidebar';

const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [expandedContact, setExpandedContact] = useState(null);
  const [contactDocuments, setContactDocuments] = useState({});
  const [loadingDocuments, setLoadingDocuments] = useState({});
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
    notes: ''
  });

  const loadContacts = useCallback(async () => {
    try {
      const response = await getContacts();
      setContacts(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const loadContactDocuments = async (contactId) => {
    if (contactDocuments[contactId]) return; // Already loaded
    
    setLoadingDocuments(prev => ({ ...prev, [contactId]: true }));
    try {
      const response = await getContactDocuments(contactId);
      setContactDocuments(prev => ({ ...prev, [contactId]: response.data }));
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoadingDocuments(prev => ({ ...prev, [contactId]: false }));
    }
  };

  const toggleContact = (contactId) => {
    if (expandedContact === contactId) {
      setExpandedContact(null);
    } else {
      setExpandedContact(contactId);
      loadContactDocuments(contactId);
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.name) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }

    try {
      await createContact(newContact);
      toast.success('Kontakt erstellt');
      setNewContactOpen(false);
      setNewContact({ name: '', email: '', phone: '', address: '', company: '', notes: '' });
      loadContacts();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleDeleteContact = async (contactId, e) => {
    e.stopPropagation();
    if (!confirm('Kontakt wirklich löschen?')) return;

    try {
      await deleteContact(contactId);
      setContacts(contacts.filter(c => c.id !== contactId));
      if (expandedContact === contactId) setExpandedContact(null);
      toast.success('Kontakt gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.phone?.includes(query) ||
      c.address?.toLowerCase().includes(query)
    );
  });

  const groupedContacts = filteredContacts.reduce((acc, contact) => {
    const letter = contact.name?.charAt(0)?.toUpperCase() || '#';
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(contact);
    return acc;
  }, {});

  const sortedLetters = Object.keys(groupedContacts).sort();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${document.documentElement.classList.contains('dark') ? 'galaxy-bg' : 'bg-background'}`} data-testid="contacts-page">
      {/* Light Mode Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none dark:hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="flex relative z-10">
        <Sidebar />

        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="glass-panel sticky top-0 z-40 border-b border-white/10 dark:bg-white/5 dark:border-white/10">
            <div className="px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-violet-500" />
                <h1 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Kontakte & Kunden
                </h1>
              </div>

              <div className="flex items-center gap-3 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Kontakte suchen..."
                    className="pl-10 h-10 rounded-xl glass-input dark:bg-white/10 dark:border-white/20"
                    data-testid="contacts-search-input"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                      <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
              </div>

              <Dialog open={newContactOpen} onOpenChange={setNewContactOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl gap-2 shadow-lg shadow-violet-500/25">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Neuer Kontakt</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass-panel border-white/20 dark:bg-gray-900/95 dark:border-white/10">
                  <DialogHeader>
                    <DialogTitle className="text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Neuen Kontakt anlegen</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-foreground">Name *</Label>
                        <Input
                          value={newContact.name}
                          onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                          placeholder="Max Mustermann"
                          className="mt-2 glass-input dark:bg-white/10 dark:border-white/20"
                          data-testid="new-contact-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Firma</Label>
                        <Input
                          value={newContact.company}
                          onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                          placeholder="Firma GmbH"
                          className="mt-2 glass-input dark:bg-white/10 dark:border-white/20"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-foreground">E-Mail</Label>
                      <Input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        placeholder="max@beispiel.de"
                        className="mt-2 glass-input dark:bg-white/10 dark:border-white/20"
                        data-testid="new-contact-email-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-foreground">Telefon</Label>
                        <Input
                          type="tel"
                          value={newContact.phone}
                          onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                          placeholder="+49 30 123456"
                          className="mt-2 glass-input dark:bg-white/10 dark:border-white/20"
                          data-testid="new-contact-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-foreground">Adresse</Label>
                        <Input
                          value={newContact.address}
                          onChange={(e) => setNewContact({ ...newContact, address: e.target.value })}
                          placeholder="Musterstr. 1, 10115 Berlin"
                          className="mt-2 glass-input dark:bg-white/10 dark:border-white/20"
                          data-testid="new-contact-address-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-foreground">Notizen</Label>
                      <Textarea
                        value={newContact.notes}
                        onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                        placeholder="Zusätzliche Informationen..."
                        className="mt-2 glass-input dark:bg-white/10 dark:border-white/20"
                        rows={3}
                      />
                    </div>
                    <Button onClick={handleCreateContact} className="w-full bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl shadow-lg shadow-violet-500/25" data-testid="new-contact-submit-btn">
                      Kontakt speichern
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </header>

          {/* Main Content - Phonebook Style */}
          <div className="p-6">
            <div className="max-w-4xl mx-auto">
              {/* Stats Header */}
              <div className="glass-card rounded-2xl p-4 mb-6 dark:bg-white/5 dark:border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{filteredContacts.length}</p>
                      <p className="text-sm text-muted-foreground">Kontakte gesamt</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Alphabetisch sortiert</p>
                    <p className="text-xs text-muted-foreground/60">Klicken zum Aufklappen</p>
                  </div>
                </div>
              </div>

              {/* Contact List */}
              <div className="space-y-4">
                {sortedLetters.length === 0 ? (
                  <div className="glass-card rounded-2xl p-12 text-center dark:bg-white/5">
                    <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                    <p className="text-lg font-medium text-foreground mb-2">Keine Kontakte gefunden</p>
                    <p className="text-muted-foreground mb-6">Lade Dokumente hoch, um automatisch Kontakte zu erstellen</p>
                    {searchQuery && (
                      <Button variant="outline" onClick={() => setSearchQuery('')} className="rounded-xl">
                        Suche zurücksetzen
                      </Button>
                    )}
                  </div>
                ) : (
                  sortedLetters.map(letter => (
                    <div key={letter} className="space-y-2">
                      {/* Letter Header */}
                      <div className="flex items-center gap-3 px-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-violet-500/25">
                          {letter}
                        </div>
                        <div className="flex-1 h-px bg-gradient-to-r from-violet-500/30 to-transparent"></div>
                        <span className="text-xs text-muted-foreground">{groupedContacts[letter].length} Kontakt{groupedContacts[letter].length !== 1 ? 'e' : ''}</span>
                      </div>

                      {/* Contacts in this letter group */}
                      <div className="space-y-2 ml-14">
                        {groupedContacts[letter].map((contact, idx) => (
                          <motion.div 
                            key={contact.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            className="glass-card rounded-2xl overflow-hidden dark:bg-white/5 dark:border-white/10"
                          >
                            {/* Contact Header - Always Visible */}
                            <div 
                              className="p-4 cursor-pointer hover:bg-white/50 dark:hover:bg-white/10 transition-colors"
                              onClick={() => toggleContact(contact.id)}
                              data-testid={`contact-card-${contact.id}`}
                            >
                              <div className="flex items-center gap-4">
                                {/* Avatar */}
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-semibold text-lg shadow-lg">
                                  {contact.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>

                                {/* Main Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-foreground truncate">{contact.name}</h3>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400">
                                      #{filteredContacts.indexOf(contact) + 1}
                                    </span>
                                  </div>
                                  
                                  {/* Quick Info Row */}
                                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                    {contact.email && (
                                      <span className="flex items-center gap-1 truncate">
                                        <Mail className="w-3 h-3 text-blue-500" />
                                        {contact.email}
                                      </span>
                                    )}
                                    {contact.phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3 text-emerald-500" />
                                        {contact.phone}
                                      </span>
                                    )}
                                    {!contact.email && !contact.phone && (
                                      <span className="text-muted-foreground/50 italic">Keine Kontaktdaten</span>
                                    )}
                                  </div>
                                </div>

                                {/* Expand Icon */}
                                <motion.div
                                  animate={{ rotate: expandedContact === contact.id ? 180 : 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                </motion.div>
                              </div>
                            </div>

                            {/* Expanded Content */}
                            <AnimatePresence>
                              {expandedContact === contact.id && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="border-t border-white/10 dark:border-white/5">
                                    {/* Contact Details - Unified Grid Layout */}
                                    <div className="p-4 bg-gradient-to-b from-violet-50/50 to-transparent dark:from-violet-500/5 dark:to-transparent">
                                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Kontaktdaten</h4>
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {/* Firma */}
                                        <div className="bg-white/60 dark:bg-white/5 rounded-xl p-3 border border-white/20 dark:border-white/10">
                                          <div className="flex items-center gap-2 mb-1">
                                            <Building2 className="w-3.5 h-3.5 text-violet-500" />
                                            <p className="text-xs text-muted-foreground">Firma</p>
                                          </div>
                                          <p className="text-sm font-medium text-foreground truncate">{contact.name || '-'}</p>
                                        </div>
                                        
                                        {/* E-Mail */}
                                        <div className="bg-white/60 dark:bg-white/5 rounded-xl p-3 border border-white/20 dark:border-white/10">
                                          <div className="flex items-center gap-2 mb-1">
                                            <Mail className="w-3.5 h-3.5 text-blue-500" />
                                            <p className="text-xs text-muted-foreground">E-Mail</p>
                                          </div>
                                          {contact.email ? (
                                            <a href={`mailto:${contact.email}`} className="text-sm font-medium text-blue-600 hover:underline truncate block">{contact.email}</a>
                                          ) : (
                                            <p className="text-sm text-muted-foreground/50">-</p>
                                          )}
                                        </div>
                                        
                                        {/* Telefon */}
                                        <div className="bg-white/60 dark:bg-white/5 rounded-xl p-3 border border-white/20 dark:border-white/10">
                                          <div className="flex items-center gap-2 mb-1">
                                            <Phone className="w-3.5 h-3.5 text-emerald-500" />
                                            <p className="text-xs text-muted-foreground">Telefon</p>
                                          </div>
                                          {contact.phone ? (
                                            <a href={`tel:${contact.phone}`} className="text-sm font-medium text-emerald-600 hover:underline">{contact.phone}</a>
                                          ) : (
                                            <p className="text-sm text-muted-foreground/50">-</p>
                                          )}
                                        </div>
                                        
                                        {/* Adresse */}
                                        <div className="bg-white/60 dark:bg-white/5 rounded-xl p-3 border border-white/20 dark:border-white/10 md:col-span-2">
                                          <div className="flex items-center gap-2 mb-1">
                                            <MapPin className="w-3.5 h-3.5 text-orange-500" />
                                            <p className="text-xs text-muted-foreground">Adresse</p>
                                          </div>
                                          <p className="text-sm font-medium text-foreground">{contact.address || '-'}</p>
                                        </div>
                                        
                                        {/* IBAN */}
                                        <div className="bg-white/60 dark:bg-white/5 rounded-xl p-3 border border-white/20 dark:border-white/10">
                                          <div className="flex items-center gap-2 mb-1">
                                            <CreditCard className="w-3.5 h-3.5 text-indigo-500" />
                                            <p className="text-xs text-muted-foreground">IBAN</p>
                                          </div>
                                          <p className="text-sm font-medium text-foreground font-mono">{contact.iban || '-'}</p>
                                        </div>
                                      </div>
                                      
                                      {/* Quick Actions */}
                                      <div className="flex gap-2 mt-4">
                                        {contact.email && (
                                          <a href={`mailto:${contact.email}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-sm hover:bg-blue-200 dark:hover:bg-blue-500/30 transition-colors">
                                            <Mail className="w-4 h-4" />
                                            E-Mail
                                          </a>
                                        )}
                                        {contact.phone && (
                                          <a href={`tel:${contact.phone}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm hover:bg-emerald-200 dark:hover:bg-emerald-500/30 transition-colors">
                                            <Phone className="w-4 h-4" />
                                            Anrufen
                                          </a>
                                        )}
                                        <button 
                                          onClick={(e) => handleDeleteContact(contact.id, e)}
                                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 text-sm hover:bg-red-200 dark:hover:bg-red-500/30 transition-colors ml-auto"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                          Löschen
                                        </button>
                                      </div>
                                    </div>

                                    {/* Documents Section */}
                                    <div className="p-4 border-t border-white/10 dark:border-white/5">
                                      <div className="flex items-center gap-2 mb-3">
                                        <FileText className="w-4 h-4 text-violet-500" />
                                        <h4 className="font-medium text-foreground">Dokumente von diesem Kontakt</h4>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-600">
                                          {contactDocuments[contact.id]?.length || 0}
                                        </span>
                                      </div>
                                      
                                      {loadingDocuments[contact.id] ? (
                                        <div className="flex items-center justify-center py-4">
                                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500"></div>
                                        </div>
                                      ) : contactDocuments[contact.id]?.length > 0 ? (
                                        <div className="space-y-2">
                                          {contactDocuments[contact.id].map(doc => (
                                            <div 
                                              key={doc.id} 
                                              className="flex items-center gap-3 p-3 rounded-xl bg-white/50 dark:bg-white/5 border border-white/20 dark:border-white/10 hover:bg-white/70 dark:hover:bg-white/10 transition-colors cursor-pointer group"
                                              onClick={() => doc.file_name && window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`, '_blank')}
                                              data-testid={`contact-doc-${doc.id}`}
                                            >
                                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                doc.doc_type === 'Banken' ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600' :
                                                doc.doc_type === 'Telekommunikation' ? 'bg-pink-100 dark:bg-pink-500/20 text-pink-600' :
                                                doc.doc_type === 'Versicherungen' ? 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600' :
                                                doc.doc_type === 'Bußgelder' ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-600' :
                                                'bg-gray-100 dark:bg-gray-500/20 text-gray-600'
                                              }`}>
                                                <FileText className="w-5 h-5" />
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="font-medium text-foreground truncate text-sm">
                                                  {doc.summary || doc.doc_type || 'Dokument'}
                                                </p>
                                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                  <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10">{doc.doc_type}</span>
                                                  {doc.invoice_no && <span>Nr. {doc.invoice_no}</span>}
                                                  <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {doc.created_at ? format(new Date(doc.created_at), 'dd.MM.yyyy', { locale: de }) : '-'}
                                                  </span>
                                                </div>
                                              </div>
                                              <div className="text-right flex items-center gap-3">
                                                <div>
                                                  <p className="font-semibold text-foreground">{formatCurrency(doc.amount)}</p>
                                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                    doc.status === 'done' ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600' :
                                                    doc.status === 'urgent' ? 'bg-red-100 dark:bg-red-500/20 text-red-600' :
                                                    'bg-blue-100 dark:bg-blue-500/20 text-blue-600'
                                                  }`}>
                                                    {doc.status === 'done' ? 'Erledigt' : doc.status === 'urgent' ? 'Dringend' : 'Offen'}
                                                  </span>
                                                </div>
                                                <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-center py-4 text-muted-foreground text-sm">
                                          Keine Dokumente vorhanden
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ContactsPage;
